import React, { forwardRef, useMemo, useState } from 'react';
import { HexColorPicker } from 'react-colorful';
import { cn } from '@/lib/utils';
import type { ButtonProps } from '@/components/ui/button';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Input } from '@/components/ui/input';

interface ColorPickerProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
}

export const ColorPicker: React.FC<
  Omit<ButtonProps, 'value' | 'onChange' | 'onBlur'> & ColorPickerProps
> = ({
  disabled,
  value,
  onChange,
  onBlur,
  name,
  className,
  size,
  ...props
}) => {
  const [open, setOpen] = useState(false);

  const parsedValue = useMemo(() => {
    if (!value) return '#FFFFFF';
    const v = value.trim().toUpperCase();
    const hex = v.startsWith('#') ? v : `#${v}`;
    return hex;
  }, [value]);

  const handleChange = (newValue: string) => {
    const hex = newValue.trim().toUpperCase();
    onChange(hex.startsWith('#') ? hex : `#${hex}`);
  };

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild disabled={disabled} onBlur={onBlur}>
        <Button
          {...props}
          className={cn('h-9 w-9 p-0 border border-gray-300', className)}
          name={name}
          onClick={() => {
            setOpen(true);
          }}
          size={size}
          style={{
            backgroundColor: parsedValue,
          }}
          variant="outline"
        >
          <span className="sr-only">Selecionar cor</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 space-y-3">
        <HexColorPicker color={parsedValue} onChange={handleChange} />
        <Input
          maxLength={7}
          value={parsedValue}
          onChange={(e) => handleChange(e.currentTarget.value)}
        />
      </PopoverContent>
    </Popover>
  );
};

